// File: src/utils/standings.js
// Championship standings.
//
// Extracted from TabbedStandingsView so the arithmetic that decides who wins a
// championship can be tested directly rather than through a rendered component.

/**
 * The rounds of a series that have actually been run.
 *
 * A round exists from the moment an event class names the series - often months
 * before anyone drives it. Such a round is not a race nobody attended, it is a
 * race that has not happened, and counting it as the former is wrong twice
 * over: every driver takes a zero for it, and where `races_counted` is set that
 * zero pushes a real result out of the counting set. A season of eight rounds
 * counting the best six, with two still ahead, would quietly stop counting
 * everybody's second- and third-best drive.
 *
 * Results are what makes a round real, so that is the test.
 *
 * @param {object} series - with races[], each with rankings[]
 * @returns {Array} the races with at least one result recorded
 */
export const racesRun = (series) =>
  (series.races || []).filter(race => (race.rankings || []).length > 0);

/**
 * Build the championship table for a series.
 *
 * Every driver gets one entry per race that has been run: a real result if they
 * raced, a zero marked `missed` if they did not. `races_counted` then says how
 * many of a driver's results actually count, best first.
 *
 * @param {object} series - with drivers[], races[] (each with rankings[]),
 *                          races_counted and calculate_qualification_points
 * @returns {Array} drivers with raceScores, racePoints, qualificationPoints and
 *                  totalPoints, highest total first
 */
export const calculateStandings = (series) => {
  const driverStats = {};
  const races = racesRun(series);

  series.drivers?.forEach(driver => {
    driverStats[driver.id] = {
      name: driver.name,
      club: driver.club,
      birthYear: driver.birth_year,
      raceScores: [],
      totalPoints: 0,
      racePoints: 0,
      qualificationPoints: 0
    };
  });

  races.forEach(race => {
    race.rankings?.forEach(ranking => {
      if (driverStats[ranking.driver_id]) {
        const racePoints = ranking.points || 0;
        const qualPoints = series.calculate_qualification_points
          ? (ranking.qualification_points || 0)
          : 0;

        driverStats[ranking.driver_id].raceScores.push({
          raceId: race.id,
          raceName: race.name,
          raceDate: race.date,
          position: ranking.position,
          qualificationPosition: ranking.qualification_position,
          points: racePoints,
          qualificationPoints: qualPoints,
          total: racePoints + qualPoints,
          participated: true
        });
      }
    });
  });

  Object.values(driverStats).forEach(driver => {
    const participatedRaceIds = driver.raceScores.map(s => s.raceId);

    races.forEach(race => {
      if (!participatedRaceIds.includes(race.id)) {
        driver.raceScores.push({
          raceId: race.id,
          raceName: race.name,
          raceDate: race.date,
          position: null,
          qualificationPosition: null,
          points: 0,
          qualificationPoints: 0,
          total: 0,
          participated: false,
          missed: true
        });
      }
    });

    driver.raceScores.sort((a, b) => a.total - b.total);

    // How many results to drop, counted across EVERY race in the series - not
    // just the ones this driver entered.
    //
    // Counting only participated races let an absence shield a real result: a
    // driver who missed a round had their missed zero dropped instead of their
    // worst score, so they kept more counting results than someone who entered
    // every round. With a full field of eight races counting six, a driver who
    // missed one kept all seven of their results while everyone else kept six.
    const racesCounted = series.races_counted || 0;
    const actualDropCount = racesCounted > 0 && driver.raceScores.length > racesCounted
      ? driver.raceScores.length - racesCounted
      : 0;

    // A race someone did not enter is not a "dropped result" - it is an
    // absence, and labelling it dropped in the table reads as a penalty. It
    // scores zero either way, so leaving it unflagged changes no total.
    const droppedRaceIds = new Set(
      driver.raceScores
        .slice(0, actualDropCount)
        .filter(score => score.participated)
        .map(score => score.raceId)
    );

    driver.raceScores.forEach(score => {
      score.dropped = droppedRaceIds.has(score.raceId);
      if (!score.dropped) {
        driver.racePoints += score.points;
        driver.qualificationPoints += score.qualificationPoints;
        driver.totalPoints += score.total;
      }
    });
  });

  return Object.values(driverStats).sort((a, b) => b.totalPoints - a.totalPoints);
};
